import { useState, useEffect, useRef } from 'react';
import Card from '../components/common/Card.jsx';
import Table from '../components/common/Table.jsx';
import DonutChart from '../components/charts/DonutChart.jsx';
import ScatterChart from '../components/charts/ScatterChart.jsx';
import SimpleLineChart from '../components/charts/SimpleLineChart.jsx';
import TopActionsCard from '../components/optimization/TopActionsCard.jsx';
import AIModal from '../components/optimization/AIModal.jsx';
import { BadgeSkeleton, KpiSkeleton } from '../components/optimization/Skeletons.jsx';
import { exportCSV, CHART_COLORS, buildExportFilename, formatSavingStr, ADD_NEW_ACCOUNT, setScopedItem, STORAGE_KEYS } from '../components/common/Helper';
import { CostCell, RecCell, RecommendedCostCell, SavingCell } from '../components/optimization/RecCells.jsx';
import OptimizationService from '../services/OptimizationService';
import MultiSelect from '../components/common/MultiSelect.jsx';
import useTagList from '../hooks/useTagList';
import { useAuth } from '../state/AuthContext';
import {
  MoneyIcon, DollarIcon,
} from '../components/icons';

// ── Helpers ────────────────────────────────────────────────
const calcAgeDays = (dt) => {
  if (!dt) return null;
  return Math.floor((Date.now() - new Date(dt).getTime()) / 86400000);
};

const stateClass = (state) => {
  const s = (state ?? '').toLowerCase();
  if (s === 'completed' || s === 'in-policy' || s === 'available') return 'st-avail';
  if (s === 'pending' || s === 'archive' || s === 'creating') return 'st-creating';
  if (s === 'error') return 'st-stopped';
  if (s === 'orphaned') return 'st-detached';
  return 'st-warn';
};

// ── Row Mapper ─────────────────────────────────────────────
const resolveSnapshotRec = (recType, detail) => {
  const t = (recType ?? '').toUpperCase();
  if (t === 'ARCHIVE') {
    return {
      recType: 'ARCHIVE',
      recPillClass: 'rec-rs',
      recommendationRemark: detail || 'Move to archive tier'
    };
  }
  if (t === 'DELETE') {
    return {
      recType: 'DELETE',
      recPillClass: 'rec-id',
      recommendationRemark: detail || 'Delete snapshot'
    };
  }
  if (t === 'OPTIMAL' || t === 'OPTIMIZED' || t === 'NONE' || t === '') {
    return {
      recType: 'OPTIMAL',
      recPillClass: 'rec-ok',
      recommendationRemark: 'No actions needed'
    };
  }
  return {
    recType: recType,
    recPillClass: 'rec-rs',
    recommendationRemark: detail || ''
  };
};

const calculateSnapshotCost = (item, isEBS = true) => {
  const volumeSize = item.VolumeSize != null ? parseFloat(item.VolumeSize) : 0;
  const savingValue = item.savingCost != null ? parseFloat(item.savingCost) : 0;
  const recType = (item.recommendationType ?? '').toUpperCase();
  const region = item.Region ?? '';

  let standardPrice = isEBS ? 0.05 : 0.095;
  let archivePrice = isEBS ? 0.0125 : 0.045;

  if (region.includes('ap-southeast-2')) {
    standardPrice = isEBS ? 0.11 : 0.095;
    archivePrice = isEBS ? 0.055 : 0.045;
  }

  let costMo = volumeSize * standardPrice;

  if (savingValue > 0) {
    if (recType === 'DELETE') {
      costMo = savingValue;
    } else if (recType === 'ARCHIVE') {
      const ratio = 1 - (archivePrice / standardPrice);
      if (ratio > 0) {
        costMo = savingValue / ratio;
      }
    }
  }

  if (costMo === 0 && volumeSize > 0) {
    costMo = volumeSize * standardPrice;
  }

  const recommendedCostValue = Math.max(0, costMo - savingValue);

  return {
    costMo,
    cost: costMo > 0 ? `$${costMo.toFixed(2)}` : '—',
    costValue: costMo,
    recommendedCost: recommendedCostValue > 0 ? `$${recommendedCostValue.toFixed(2)}` : '$0.00',
    recommendedCostValue,
    savingValue
  };
};

const mapSnapshotItem = (item, index) => {
  const volumeSize = item.VolumeSize != null ? parseFloat(item.VolumeSize) : null;
  const ageDays = item.ageDays != null ? parseInt(item.ageDays, 10) : calcAgeDays(item.StartDateTime);
  const costData = calculateSnapshotCost(item, true);
  const recData = resolveSnapshotRec(item.recommendationType, item.recommendationDetail);

  return {
    id: item.SnapshotId ?? `snap-${index}`,
    snapshotId: item.SnapshotId ?? '—',
    volumeId: item.VolumeId ?? '—',
    description: item.Description ?? '—',
    state: item.State ?? '—',
    volumeSize,
    volumeSizeDisplay: volumeSize != null ? `${volumeSize} GiB` : '—',
    storageTier: item.StorageTier ?? '—',
    encrypted: item.Encrypted === true ? 'Yes' : item.Encrypted === false ? 'No' : '—',
    region: item.Region ?? '—',
    startDateTime: item.StartDateTime ?? null,
    startTimeEnum: item.StartTimeEnum ?? '',
    ageDays,
    StartMonth: item.StartMonth ?? '—',
    progress: item.Progress ?? '—',
    tags: item.Tags ?? [],
    snapshotState: item.snapshotState ?? '—',
    sourceVolumeExists: item.sourceVolumeExists ?? true,
    ...costData,
    saving: formatSavingStr(costData.savingValue),
    ...recData,
    checked: false,
  };
};

const buildLineChartData = (rows, groupByKey = 'snapshotState') => {
  if (!rows || !rows.length) return null;

  const dates = [];
  const now = new Date();
  for (let i = 29; i >= 0; i--) {
    const d = new Date();
    d.setDate(now.getDate() - i);
    dates.push(d);
  }

  const groups = [...new Set(rows.map(r => r[groupByKey]).filter(Boolean))];
  if (!groups.length) return null;

  const datasets = groups.map((group, idx) => {
    const data = dates.map(date => {
      const activeSnapshots = rows.filter(r => {
        if (r[groupByKey] !== group) return false;
        if (!r.startDateTime) return true;
        return new Date(r.startDateTime) <= date;
      });
      const totalDailySpend = activeSnapshots.reduce((sum, r) => sum + (r.costMo || 0), 0) / 30;
      return parseFloat(totalDailySpend.toFixed(2));
    });

    return {
      label: group,
      data,
      borderColor: CHART_COLORS[idx % CHART_COLORS.length],
      backgroundColor: CHART_COLORS[idx % CHART_COLORS.length] + '26',
      tension: 0.3,
      fill: true,
      borderWidth: 2,
      pointRadius: 0,
    };
  });

  return {
    labels: dates.map(d => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })),
    datasets
  };
};

// ── KPI Builder ────────────────────────────────────────────
const buildKpiData = (rows, tv = null) => {
  if (tv && tv.totalCostValue != null) {
    return [
      { id: 1, title: '0-6 Months cost',       value: `$${(tv.zeroToSixMonthsPriceValue ?? 0).toFixed(2)}`,   type: 't1', filter: 'ZERO_TO_SIX_MONTHS',    icon: <MoneyIcon /> },
      { id: 2, title: 'Less Than 1 Year cost',  value: `$${(tv.sixMonthsToOneYearPriceValue ?? 0).toFixed(2)}`, type: 't2', filter: 'SIX_MONTHS_TO_ONE_YEAR', icon: <MoneyIcon /> },
      { id: 3, title: '1+ Years cost',          value: `$${(tv.overOneYearPriceValue ?? 0).toFixed(2)}`,        type: 't3', filter: 'OVER_ONE_YEAR',          icon: <DollarIcon /> },
    ];
  }
  const groups = { ZERO_TO_SIX_MONTHS: [], SIX_MONTHS_TO_ONE_YEAR: [], OVER_ONE_YEAR: [] };
  rows.forEach((r) => { if (groups[r.startTimeEnum]) groups[r.startTimeEnum].push(r); });
  const sumCost = (list) => list.reduce((s, r) => s + (r.costMo || 0), 0);
  return [
    { id: 1, title: '0-6 Months cost',       value: `$${sumCost(groups.ZERO_TO_SIX_MONTHS).toFixed(2)}`,    type: 't1', filter: 'ZERO_TO_SIX_MONTHS',    icon: <MoneyIcon /> },
    { id: 2, title: 'Less Than 1 Year cost',  value: `$${sumCost(groups.SIX_MONTHS_TO_ONE_YEAR).toFixed(2)}`, type: 't2', filter: 'SIX_MONTHS_TO_ONE_YEAR', icon: <MoneyIcon /> },
    { id: 3, title: '1+ Years cost',          value: `$${sumCost(groups.OVER_ONE_YEAR).toFixed(2)}`,          type: 't3', filter: 'OVER_ONE_YEAR',          icon: <DollarIcon /> },
  ];
};

const buildKpiRow2 = (tv, rows = []) => {
  const d = new Date();
  const dateLabel = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const totalCost = tv?.totalCostValue ?? rows.reduce((s, r) => s + (r.costMo || 0), 0);
  const savingCost = tv?.savingCost ?? rows.reduce((s, r) => s + (r.savingValue || 0), 0);
  const recommendedCost = tv?.recommendedCost ?? rows.reduce((s, r) => s + (r.recommendedCostValue || 0), 0);
  return [
    { id: 'r2-1', title: `Total Cost (${dateLabel})`,       value: `$${totalCost.toFixed(2)}`,       type: 't4', icon: <DollarIcon /> },
    { id: 'r2-2', title: 'Recommended Saving Cost',          value: `$${savingCost.toFixed(2)}`,      type: 't3', icon: <DollarIcon /> },
    { id: 'r2-3', title: 'Total Payable (after savings)',    value: `$${recommendedCost.toFixed(2)}`, type: 't1', icon: <DollarIcon /> },
  ];
};

// ── Chart Builders ─────────────────────────────────────────

// Scatter: age (days) vs size (GiB), grouped by storage tier
const buildScatterData = (rows) => {
  if (!rows.length) return null;
  const groups = {};
  rows.forEach((r) => {
    if (r.ageDays == null || r.volumeSize == null) return;
    const key = r.snapshotState && r.snapshotState !== '—' ? r.snapshotState : 'Unknown';
    if (!groups[key]) groups[key] = [];
    groups[key].push({ x: r.ageDays, y: r.volumeSize });
  });
  const keys = Object.keys(groups);
  if (!keys.length) return null;
  return {
    datasets: keys.map((k, i) => ({
      label: k,
      data: groups[k],
      backgroundColor: CHART_COLORS[i % CHART_COLORS.length],
      pointRadius: 5,
      pointHoverRadius: 7,
    })),
  };
};

// Donut: storage (GiB) by snapshot state
const buildStateDonut = (rows) => {
  if (!rows.length) return null;
  const map = {};
  rows.forEach((r) => {
    const s = r.snapshotState && r.snapshotState !== '—' ? r.snapshotState : r.state;
    if (s && s !== '—') map[s] = (map[s] || 0) + (r.volumeSize || 0);
  });
  const keys = Object.keys(map);
  const total = keys.reduce((s, k) => s + map[k], 0);
  if (!total) return null;
  return {
    labels: keys,
    datasets: [{ data: keys.map((k) => map[k]), backgroundColor: keys.map((_, i) => CHART_COLORS[i % CHART_COLORS.length]), borderWidth: 2, borderColor: '#fff' }],
  };
};

// Donut: storage (GiB) by region
const buildRegionDonut = (rows) => {
  if (!rows.length) return null;
  const map = {};
  rows.forEach((r) => { if (r.region && r.region !== '—') map[r.region] = (map[r.region] || 0) + (r.volumeSize || 0); });
  const keys = Object.keys(map);
  const total = keys.reduce((s, k) => s + map[k], 0);
  if (!total) return null;
  return {
    labels: keys,
    datasets: [{ data: keys.map((k) => map[k]), backgroundColor: keys.map((_, i) => CHART_COLORS[i % CHART_COLORS.length]), borderWidth: 2, borderColor: '#fff' }],
  };
};

// Top actions: highest savings snapshots
const buildInsightActions = (rows) => {
  const seen = new Set();
  const sorted = [...rows]
    .filter((r) => {
      if (seen.has(r.snapshotId)) return false;
      seen.add(r.snapshotId);
      return r.savingValue > 0;
    })
    .sort((a, b) => b.savingValue - a.savingValue)
    .slice(0, 5);
  const total = sorted.reduce((s, r) => s + (r.savingValue || 0), 0);
  const actions = sorted.map((r, i) => ({
    id: i + 1,
    title: r.snapshotId,
    amount: r.savingValue > 0 ? `$${r.savingValue.toFixed(2)}/mo` : '—',
    desc: `${r.storageTier} · ${r.region} · ${r.ageDays != null ? `${r.ageDays}d old` : ''}`,
    row: r,
  }));
  return { actions, total };
};

// ── Main Component ─────────────────────────────────────────
const EBSSnapshotOptimization = () => {
  const { selectedAccount } = useAuth();

  const [isLoading, setIsLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [kpiData, setKpiData] = useState([]);
  const [kpiRow2Data, setKpiRow2Data] = useState([]);
  const [activeKpiFilter, setActiveKpiFilter] = useState('ALL');
  const [apiTotalPriceValue, setApiTotalPriceValue] = useState(null);

  const [scatterData, setScatterData] = useState(null);
  const [stateDonut, setStateDonut] = useState(null);
  const [regionDonut, setRegionDonut] = useState(null);
  const [insightActions, setInsightActions] = useState([]);
  const [insightTotal, setInsightTotal] = useState(0);
  const [lineChartData, setLineChartData] = useState(null);

  const { tagList } = useTagList('EBS_SNAPSHOT');
  const [filterList, setFilterList] = useState({ tag: '', values: [] });

  const [regionFilter, setRegionFilter] = useState('All regions');
  const [tierFilter, setTierFilter] = useState('All');

  const [aiOpen, setAiOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiData, setAiData] = useState(null);

  const tableRef = useRef(null);
  const scrollToTable = () => tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const handleDetailsClick = (row) => {
    setAiData({
      snapshotId: row.snapshotId, volumeId: row.volumeId,
      state: row.snapshotState, volumeSize: row.volumeSizeDisplay, ageDays: row.ageDays,
      region: row.region, storageTier: row.storageTier, encrypted: row.encrypted,
      currentCost: row.cost, saving: row.savingValue > 0 ? `$${row.savingValue.toFixed(2)}` : '—',
    });
    setAiPrompt('You are a cloud cost advisor. Based on the EBS snapshot data provided, give a short, friendly insight about this snapshot — its age, storage tier, and whether it might be a candidate for archival or deletion to save costs.');
    setAiOpen(true);
  };

  const toggleRowChecked = (id) => setRows((prev) => prev.map((r) => r.id === id ? { ...r, checked: !r.checked } : r));

  const handleFilterChange = (field, value) => {
    if (field === 'tag') { setFilterList({ tag: value ?? '', values: [] }); return; }
    if (field === 'values') setFilterList((prev) => ({ tag: prev.tag, values: Array.from(value) }));
  };

  const fetchData = (isForceRefresh = false) => {
    if (!selectedAccount) return;
    setIsLoading(true);
    setRows([]);
    OptimizationService.getTabularData(selectedAccount, 'CUSTOM_EBS_SNAPSHOT', null, isForceRefresh)
      .then((data) => {
        const mapped = (data?.rows ?? []).map(mapSnapshotItem);
        setRows(mapped);
        setApiTotalPriceValue(data?.totalPriceValue ?? null);
      })
      .finally(() => setIsLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (selectedAccount && selectedAccount !== ADD_NEW_ACCOUNT) { setFilterList({ tag: '', values: [] }); fetchData(); } }, [selectedAccount]);

  useEffect(() => {
    setKpiData(buildKpiData(rows, apiTotalPriceValue));
    setKpiRow2Data(buildKpiRow2(apiTotalPriceValue, rows));
    setScatterData(buildScatterData(rows));
    setStateDonut(buildStateDonut(rows));
    setRegionDonut(buildRegionDonut(rows));
    const { actions, total } = buildInsightActions(rows);
    setInsightActions(actions);
    setInsightTotal(total);
    setLineChartData(buildLineChartData(rows, 'snapshotState'));
  }, [rows, apiTotalPriceValue]);

  // Keep AI chatbot context in sync with the data shown on this page
  useEffect(() => {
    setScopedItem(STORAGE_KEYS.AI_CHATBOT_REQUEST_DATA, {
      page: 'EBS Snapshot Optimization',
      totalPriceValue: apiTotalPriceValue,
      ebsSnapshots: rows,
    });
  }, [rows, apiTotalPriceValue]);

  useEffect(() => { setActiveKpiFilter('ALL'); setApiTotalPriceValue(null); }, [selectedAccount]);

  const handleKpiClick = (filter) => setActiveKpiFilter((prev) => prev === filter ? 'ALL' : filter);

  const filteredRows = rows.filter((r) => {
    if (activeKpiFilter !== 'ALL' && r.startTimeEnum !== activeKpiFilter) return false;
    if (regionFilter !== 'All regions' && r.region !== regionFilter) return false;
    if (tierFilter !== 'All' && r.storageTier !== tierFilter) return false;
    if (filterList.tag && filterList.values.length > 0) {
      const hasTag = (r.tags ?? []).some(
        (t) => (t.Key ?? t.key) === filterList.tag && filterList.values.includes(t.Value ?? t.value)
      );
      if (!hasTag) return false;
    }
    return true;
  });

  const uniqueRegions = [...new Set(rows.map((r) => r.region).filter((r) => r && r !== '—'))].sort();
  const uniqueTiers = [...new Set(rows.map((r) => r.storageTier).filter((t) => t && t !== '—'))].sort();

  const selectedCount = filteredRows.filter((r) => r.checked).length;
  const allChecked = filteredRows.length > 0 && filteredRows.every((r) => r.checked);

  const toggleAllChecked = () => {
    const nowAll = allChecked;
    setRows((prev) => prev.map((r) => ({ ...r, checked: !nowAll })));
  };

  const snapshotColumns = [
    {
      key: 'checkbox',
      label: <span className={`checkbox ${allChecked ? 'checked' : ''}`} onClick={toggleAllChecked} />,
      className: 'center',
      render: (row) => (
        <span
          className={`checkbox ${row.checked ? 'checked' : ''}`}
          onClick={(e) => { e.stopPropagation(); toggleRowChecked(row.id); }}
        />
      ),
    },
    { key: 'snapshotId', label: 'Snapshot ID' },
    { key: 'volumeId', label: 'Source Volume' },
    {
      key: 'snapshotState', label: 'State',
      render: (row) => <span className={`status-pill ${stateClass(row.snapshotState)}`}>{row.snapshotState}</span>,
    },
    { key: 'volumeSizeDisplay', label: 'Size', className: 'num' },
    {
      key: 'ageDays', label: 'Age', className: 'num',
      render: (row) => row.ageDays != null ? `${row.ageDays} d` : '—',
    },
    { key: 'region', label: 'Region' },
    { key: 'StartMonth', label: 'Backup' },
    { key: 'cost', label: 'Cost / Mo', className: 'num', render: (row) => <CostCell row={row} /> },
    { key: 'recommendation', label: 'Recommendation', render: (row) => <RecCell row={row} /> },
    { key: 'saving', label: 'Saving / Mo', className: 'num', render: (row) => <SavingCell row={row} /> },
    { key: 'recommendedCost', label: 'Payable Cost / Mo', className: 'num', render: (row) => <RecommendedCostCell row={row} /> },
    {
      key: 'action', label: 'Action',
      render: (row) => <button className="mini-btn ai-assist" onClick={() => handleDetailsClick(row)}>Assist with AI</button>,
    },
  ];

  const handleExport = () =>
    exportCSV(
      filteredRows,
      snapshotColumns.filter((c) => typeof c.label === 'string'),
      buildExportFilename('ebs_snapshot_optimization', selectedAccount),
    );

  return (
    <>
      <div className="breadcrumb">
        Optimization <span style={{ color: 'var(--muted-2)' }}>›</span> <strong>EBS Snapshot</strong>
      </div>

      <div className="page-head">
        <div>
          <h1>
            EBS Snapshot Optimization{' '}
            {isLoading ? <BadgeSkeleton /> : <span className="badge">{rows.length} snapshots</span>}
          </h1>
          <div className="meta">Find orphaned, redundant, and aged snapshots — and enforce lifecycle policies</div>
        </div>
        <div className="head-actions">
          <button className="btn ghost" onClick={handleExport} disabled={filteredRows.length === 0}>⬇ Export</button>
          <button className="btn ghost" onClick={() => fetchData(true)}>↻ Refresh</button>
        </div>
      </div>

      {/* KPI Cards */}
      {isLoading ? (
        <>
          <KpiSkeleton count={3} cols={3} />
          <KpiSkeleton count={3} cols={3} />
        </>
      ) : (
        <>
          <div className="kpi-row cols-3">
            {kpiRow2Data.map((item) => (
              <div key={item.id} className={`kpi ${item.type}`}>
                <div className="ico">{item.icon}</div>
                <div>
                  <div className="lbl">{item.title}</div>
                  <div className="val">{item.value}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="kpi-row cols-3" style={{ marginTop: '8px' }}>
            {kpiData.map((item) => (
              <div
                key={item.id}
                className={`kpi ${item.type}${activeKpiFilter === item.filter ? ' kpi--active' : ''}`}
                onClick={() => handleKpiClick(item.filter)}
                style={{ cursor: 'pointer', outline: activeKpiFilter === item.filter ? '2px solid var(--brand)' : 'none', outlineOffset: '-2px' }}
              >
                <div className="ico">{item.icon}</div>
                <div>
                  <div className="lbl">{item.title}</div>
                  <div className="val">{item.value}</div>
                  <span
                    style={{ fontSize: '12px', color: 'var(--brand)', textDecoration: 'underline', textDecorationStyle: 'dotted', cursor: 'pointer' }}
                    onClick={(e) => { e.stopPropagation(); handleKpiClick(item.filter); scrollToTable(); }}
                  >View details</span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

    

      {/* Top Charts Row — grid-3: Scatter | State Donut | Top Actions */}
      <div className="grid-3">
        <Card className="first" title="Snapshot age vs size" subtitle="Each dot is a snapshot — old, large snapshots are deletion candidates">
          <ScatterChart
            data={scatterData}
            loading={isLoading}
            xLabel="Age (days)"
            yLabel="Snapshot size (GiB)"
            tooltipCallback={(c) => `${c.dataset.label}: ${c.parsed.x} d · ${c.parsed.y} GiB`}
          />
        </Card>
        <Card className="first" title="Storage by snapshot state" subtitle="Orphaned, redundant, retained, in-policy">
          <DonutChart data={stateDonut} loading={isLoading} />
        </Card>
        <TopActionsCard
          title="Top snapshot actions"
          actions={insightActions}
          total={insightTotal}
          loading={isLoading}
          entityName="snapshots"
          onAction={(item) => item.row && handleDetailsClick(item.row)}
        />
      </div>

      {/* Filter Bar */}
      <div className="filter-bar">
        <div className="field">
          <label>Filter Tag</label>
          <select value={filterList.tag} onChange={(e) => handleFilterChange('tag', e.target.value)}>
            <option value="">None</option>
            {tagList.map((tag) => <option key={tag?.Key} value={tag?.Key}>{tag?.Key}</option>)}
          </select>
        </div>
        <div className="field">
          <label>Filter Tag Values</label>
          <MultiSelect
            options={tagList.find((t) => t?.Key === filterList.tag)?.Value ?? []}
            value={filterList.values}
            onChange={(vals) => handleFilterChange('values', vals)}
            disabled={!filterList.tag}
          />
        </div>
        {/* <div className="field">
          <label>Region</label>
          <select value={regionFilter} onChange={(e) => setRegionFilter(e.target.value)}>
            <option value="All regions">All regions</option>
            {uniqueRegions.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div> */}
        {/* <div className="field">
          <label>Storage Tier</label>
          <select value={tierFilter} onChange={(e) => setTierFilter(e.target.value)}>
            <option value="All">All</option>
            {uniqueTiers.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div> */}
        <div className="spacer" />
        <button className="btn ghost" onClick={() => { setFilterList({ tag: '', values: [] }); setRegionFilter('All regions'); setTierFilter('All'); setActiveKpiFilter('ALL'); }}>✕ Clear</button>
        <button className="btn ghost" onClick={() => fetchData(true)}>↻ Refresh</button>
      </div>

      {/* Bulk Action Bar */}
      {selectedCount > 0 && (
        <div className="bulk-bar">
          <span><strong>{selectedCount} snapshots selected</strong></span>
          <button className="btn ghost">📦 Archive</button>
          <button className="btn ghost">⏱ Set Lifecycle</button>
          <button className="btn primary">🗑 Delete</button>
        </div>
      )}

      {/* Table */}
      <div ref={tableRef}>
        <Table
          columns={snapshotColumns}
          rows={filteredRows}
          loading={isLoading}
          defaultPageSize={25}
          showToolbar
          onExport={handleExport}
          defaultSortKey="savingValue"
          defaultSortDir="desc"
          emptyText="No EBS snapshots found."
          subscriptionGated
        />
      </div>

      {/* Bottom Charts Row — grid-2: Line Trend | Region Donut */}
      <div className="grid-2" style={{ marginTop: '14px' }}>
        <Card className="first" title="Snapshot storage growth — last 30 days" subtitle="Daily snapshot spend by lifecycle state">
          <SimpleLineChart data={lineChartData} loading={isLoading} />
        </Card>
        <Card className="first" title="Snapshots by region" subtitle="Storage cost share">
          <DonutChart data={regionDonut} loading={isLoading} />
        </Card>
      </div>

      <AIModal
        open={aiOpen}
        onClose={() => { setAiOpen(false); setAiPrompt(''); setAiData(null); }}
        prompt={aiPrompt}
        data={aiData}
      />
    </>
  );
};

export default EBSSnapshotOptimization;
